/**
 * Adapters for the debt, loan and date-helper contract functions (spread into `registry`).
 * They only translate JSON ⇄ domain values; output shapes mirror ContractRegistry.swift.
 */
import { formatDay } from './core/calendar.ts';
import { ZERO } from './core/decimal.ts';
import {
  analyzeDebt, analyzePayoff, rankDebts, recommendTargetDebt, type PayoffAnalysis,
} from './calculations/debt-payoff-analyzer.ts';
import {
  payoffDate, projectDebts, remainingBalance, totalCurrentDebt, totalDebtCost, totalInterestPaid,
} from './calculations/debt-projector.ts';
import { calculateDebtVsInvestment, type StrategyPoint } from './calculations/debt-vs-investment.ts';
import { ageInYears, monthsUntil, projectedAge } from './calculations/dates.ts';
import {
  balanceAfterPaymentsAtAnnualRate, estimatedBalance, linearPaydown, monthlyPayment, payoff, scheduledBalance,
} from './calculations/loan-math.ts';
import { ukStampDuty } from './calculations/stamp-duty.ts';
import { decodeProperty, decodeTransaction, decodeTransactions, decodeWindow, encodeSeries } from './codec/entities.ts';
import {
  contextFrom, decodeDay, decodeDecimal, encodeDecimal, encodeDouble, encodeOptionalDay, encodeOptionalDecimal,
  optionalDecimal,
} from './codec/scalars.ts';
import {
  debtBalance, debtMonthlyPayment, effectiveDebtPrincipal, effectiveDebtTermMonths,
} from './models/debt-transaction.ts';
import { calculateMortgageBalance } from './models/mortgage.ts';
import type { Registry } from './registry.ts';
import type * as Wire from './types/contract.generated.ts';

const encodePayoffAnalysis = (a: PayoffAnalysis): Wire.PayoffAnalysis => ({
  currentBalance: encodeDecimal(a.currentBalance),
  interestRate: encodeDecimal(a.interestRate),
  currentMonthlyPayment: encodeDecimal(a.currentMonthlyPayment),
  currentPayoffDate: formatDay(a.currentPayoffDate),
  currentTotalInterest: encodeDecimal(a.currentTotalInterest),
  currentMonthsRemaining: a.currentMonthsRemaining,
  acceleratedPayoffDate: formatDay(a.acceleratedPayoffDate),
  acceleratedTotalInterest: encodeDecimal(a.acceleratedTotalInterest),
  acceleratedMonthsRemaining: a.acceleratedMonthsRemaining,
  monthsSaved: a.monthsSaved,
  interestSaved: encodeDecimal(a.interestSaved),
  yearsSaved: encodeDouble(a.yearsSaved),
});

const encodeOptionalAnalysis = (a: PayoffAnalysis | null): Wire.PayoffAnalysis | null =>
  a === null ? null : encodePayoffAnalysis(a);

const encodeStrategyPoints = (points: readonly StrategyPoint[]) => ({
  debt: points.map((p) => encodeDecimal(p.debt)),
  investment: points.map((p) => encodeDecimal(p.investment)),
  netWorth: points.map((p) => encodeDecimal(p.netWorth)),
  totalInterestPaid: points.map((p) => encodeDecimal(p.totalInterestPaid)),
});

const encodeStrategyPoint = (p: StrategyPoint) => ({
  month: p.month,
  debt: encodeDecimal(p.debt),
  investment: encodeDecimal(p.investment),
  netWorth: encodeDecimal(p.netWorth),
  totalInterestPaid: encodeDecimal(p.totalInterestPaid),
});

export const debtRegistry: Registry = {
  'DebtProjector.project': (input) =>
    encodeSeries(projectDebts(decodeTransactions(input.transactions), decodeWindow(input.window), contextFrom(input).now)),

  'DebtProjector.calculatePayoffDate': (input) => ({
    payoffDate: encodeOptionalDay(
      payoffDate(decodeTransaction(input.transaction), decodeDay(input.startDate), contextFrom(input).now),
    ),
  }),

  'DebtProjector.calculateTotalInterestPaid': (input) => ({
    totalInterest: encodeDecimal(
      totalInterestPaid(decodeTransaction(input.transaction), decodeWindow(input.window), contextFrom(input).now),
    ),
  }),

  'DebtProjector.calculateTotalCurrentDebt': (input) => ({
    total: encodeDecimal(totalCurrentDebt(decodeTransactions(input.transactions), decodeDay(input.date), contextFrom(input).now)),
  }),

  'DebtProjector.calculateRemainingBalance': (input) => ({
    balance: encodeDecimal(remainingBalance(decodeTransaction(input.transaction), decodeDay(input.date), contextFrom(input).now)),
  }),

  'DebtProjector.calculateTotalDebtCost': (input) => ({
    totalCost: encodeDecimal(totalDebtCost(decodeTransaction(input.transaction), decodeDay(input.startDate), contextFrom(input).now)),
  }),

  'DebtPayoffAnalyzer.analyze': (input) => {
    const r = analyzePayoff(
      decodeTransactions(input.debts),
      decodeDecimal(input.extraMonthlyPayment),
      input.strategy,
      decodeDay(input.date),
      contextFrom(input).now,
    );
    return {
      rankedDebts: r.rankedDebts.map((ranked) => ({
        id: ranked.debt.id,
        rank: ranked.rank,
        currentBalance: encodeDecimal(ranked.currentBalance),
        interestRate: encodeDecimal(ranked.interestRate),
        monthlyPayment: encodeDecimal(ranked.monthlyPayment),
        analysis: encodeOptionalAnalysis(ranked.analysis),
      })),
      strategy: r.strategy,
      extraMonthlyPayment: encodeDecimal(r.extraMonthlyPayment),
      recommendedTargetDebt: r.recommendedTargetDebt?.id ?? null,
      totalCurrentDebt: encodeDecimal(r.totalCurrentDebt),
      totalInterestSaved: encodeDecimal(r.totalInterestSaved),
      totalMonthsSaved: r.totalMonthsSaved,
      totalYearsSaved: encodeDouble(r.totalYearsSaved),
      newDebtFreeDate: encodeOptionalDay(r.newDebtFreeDate),
      currentDebtFreeDate: encodeOptionalDay(r.currentDebtFreeDate),
    };
  },

  'DebtPayoffAnalyzer.analyzeDebt': (input) => ({
    analysis: encodeOptionalAnalysis(
      analyzeDebt(decodeTransaction(input.debt), decodeDecimal(input.extraMonthlyPayment), decodeDay(input.date), contextFrom(input).now),
    ),
  }),

  'DebtPayoffAnalyzer.rankDebts': (input) => {
    const debts = decodeTransactions(input.debts);
    const date = decodeDay(input.date);
    const now = contextFrom(input).now;
    return {
      ranked: rankDebts(debts, input.strategy, date, now).map((debt) => debt.id),
      recommendedTarget: recommendTargetDebt(debts, input.strategy, date, now)?.id ?? null,
    };
  },

  'DebtVsInvestmentCalculator.calculate': (input) => {
    const r = calculateDebtVsInvestment({
      debtAmount: decodeDecimal(input.debtAmount),
      debtInterestRate: decodeDecimal(input.debtInterestRate),
      investmentReturn: decodeDecimal(input.investmentReturn),
      monthlyPayment: decodeDecimal(input.monthlyPayment),
      totalMonthlyAllocation: decodeDecimal(input.totalMonthlyAllocation),
      years: input.years,
      maxMonths: input.maxMonths ?? 360,
    });
    return {
      aggressive: encodeStrategyPoints(r.aggressiveData),
      invest: encodeStrategyPoints(r.investData),
      aggressiveDebtFreeMonth: r.aggressiveDebtFreeMonth,
      investDebtFreeMonth: r.investDebtFreeMonth,
      crossoverMonth: r.crossoverMonth,
      aggressiveTargetYear: encodeStrategyPoint(r.aggressiveTargetYear),
      investTargetYear: encodeStrategyPoint(r.investTargetYear),
      maxNetWorth: encodeDecimal(r.maxNetWorth),
      opportunityCost: encodeDecimal(r.opportunityCost),
      timeDifference: r.timeDifference,
      aggressiveMonthlyAmount: encodeDecimal(r.aggressiveMonthlyAmount),
      investMonthlyAmount: encodeDecimal(r.investMonthlyAmount),
      targetMonths: r.targetMonths,
      aggressiveTotalInterestPaid: encodeDecimal(r.aggressiveTotalInterestPaid),
      investTotalInterestPaid: encodeDecimal(r.investTotalInterestPaid),
      winner: r.winner,
    };
  },

  'Transaction.debtMaths': (input) => {
    const transaction = decodeTransaction(input.transaction);
    return {
      debtBalance: encodeOptionalDecimal(debtBalance(transaction, decodeDay(input.date))),
      monthlyPayment: encodeOptionalDecimal(debtMonthlyPayment(transaction)),
      effectiveDebtPrincipal: encodeOptionalDecimal(effectiveDebtPrincipal(transaction)),
      effectiveDebtTermMonths: effectiveDebtTermMonths(transaction),
    };
  },

  'Plan.calculateMortgageBalance': (input) => {
    const holder = decodeProperty({
      id: 'mortgage-holder',
      startDate: input.mortgage.startDate,
      propertyValue: '0',
      mortgage: input.mortgage,
    });
    return { balance: encodeDecimal(calculateMortgageBalance(holder.mortgage, decodeDay(input.date))) };
  },

  'LoanMath.monthlyPayment': (input) => ({
    payment: encodeOptionalDecimal(
      monthlyPayment({
        principal: decodeDecimal(input.principal),
        annualRatePercent: decodeDecimal(input.annualRatePercent),
        termMonths: input.termMonths,
        balloon: optionalDecimal(input.balloon) ?? ZERO,
        interestOnly: input.interestOnly ?? false,
      }),
    ),
  }),

  'LoanMath.balanceAfterPayments': (input) => ({
    balance: encodeDecimal(
      balanceAfterPaymentsAtAnnualRate(
        decodeDecimal(input.principal),
        decodeDecimal(input.monthlyPayment),
        decodeDecimal(input.annualRatePercent),
        input.paymentsMade,
      ),
    ),
  }),

  'LoanMath.scheduledBalance': (input) => ({
    balance: encodeDecimal(
      scheduledBalance({
        principal: decodeDecimal(input.principal),
        annualRatePercent: decodeDecimal(input.annualRatePercent),
        termMonths: input.termMonths,
        paymentsMade: input.paymentsMade,
        interestOnly: input.interestOnly ?? false,
      }),
    ),
  }),

  'LoanMath.estimatedBalance': (input) => {
    const originalLoan = decodeDecimal(input.originalLoan);
    return {
      balance: encodeDecimal(
        estimatedBalance({
          originalLoan,
          monthlyPayment: decodeDecimal(input.monthlyPayment),
          annualRatePercent: decodeDecimal(input.annualRatePercent),
          monthsElapsed: input.monthsElapsed,
        }),
      ),
      linearPaydown: encodeDecimal(linearPaydown(originalLoan, input.monthsElapsed)),
    };
  },

  'LoanMath.payoff': (input) => {
    const result = payoff(
      decodeDecimal(input.balance),
      decodeDecimal(input.annualRatePercent),
      decodeDecimal(input.monthlyPayment),
      input.maxMonths ?? 600,
    );
    return {
      payoff:
        result === null
          ? null
          : {
              months: result.months,
              totalPaid: encodeDecimal(result.totalPaid),
              interestPaid: encodeDecimal(result.interestPaid),
              isPaidOff: result.isPaidOff,
            },
    };
  },

  'StampDuty.uk': (input) => ({
    duty: encodeDecimal(ukStampDuty(decodeDecimal(input.price), input.isFirstTimeBuyer ?? false)),
  }),

  'Age.years': (input) => ({
    age: ageInYears(input.birthYear, input.birthMonth ?? null, decodeDay(input.date)),
  }),

  'Age.projected': (input) => ({
    age: projectedAge(input.currentAge, input.birthMonth ?? null, decodeDay(input.date), contextFrom(input).now),
  }),

  'DateMath.monthsUntil': (input) => ({
    months: monthsUntil(decodeDay(input.date), contextFrom(input).now),
  }),
};
